import { jsPDF } from 'jspdf';

interface Player {
  id: string;
  firstName?: string;
  lastName?: string;
  user?: {
    firstName?: string;
    lastName?: string;
  };
  academyName?: string;
}

interface RankingStrokeType {
  code: string;
  courtPosition: string;
  halfSide: string;
  strokeType: string;
  strokeAction: string;
  shotType: string;
  placement: string;
}

// Helper function to convert half court values to shorter format
const normalizeHalfCourt = (halfSide: string): string => {
  if (!halfSide) return 'N/A';
  const side = halfSide.toLowerCase().trim();
  if (side.includes('right service court') || side.includes('right side')) {
    return 'Right Side';
  }
  if (side.includes('left service court') || side.includes('left side')) {
    return 'Left Side';
  }
  return halfSide; // Return original if no match
};

export const generateRankingEvaluationManualForm = async (
  player: Player,
  selectedSport: string,
  categoryLabel: string,
  coachName: string = 'N/A',
  evaluationDate?: string
) => {
  try {
    // Fetch ranking stroke types from API
    const url = `/api/admin/ranking-stroke-types?t=${Date.now()}`;
    const response = await fetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', errorText);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success || !result.data || !Array.isArray(result.data)) {
      console.error('Invalid API response:', result);
      throw new Error('No ranking stroke types found');
    }

    // Filter active strokes only
    const strokeTypes: RankingStrokeType[] = result.data
      .filter((st: any) => st.isActive !== false)
      .map((st: any) => ({
        code: st.code || '',
        courtPosition: st.courtPosition || '',
        halfSide: st.halfSide || '',
        strokeType: st.strokeType || '',
        strokeAction: st.strokeAction || '',
        shotType: st.shotType || '',
        placement: st.placement || '',
      }));
    
    if (strokeTypes.length === 0) {
      alert(`No ranking stroke types defined. Cannot generate form.`);
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const footerHeight = 15;
    const headerHeight = 20;
    let yPosition = headerHeight;
    const rowHeight = 6;
    const tableStartX = margin;

    // Header with branding
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Encycdata', margin, 8);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('EncycGames', margin, 13);
    
    // Assessment title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(
      `Ranking Evaluation Manual Assessment Form - ${categoryLabel}`,
      pageWidth / 2,
      yPosition,
      { align: 'center' }
    );
    yPosition += 8;

    // Header Information
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const playerName = `${player?.firstName || player?.user?.firstName || ''} ${player?.lastName || player?.user?.lastName || ''}`.trim();
    const academyName = player?.academyName || 'N/A';
    // Ensure coachName is always defined
    const displayCoachName = coachName || 'N/A';
    const reportDate = evaluationDate 
      ? new Date(evaluationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    
    doc.text(`Player: ${playerName}`, margin, yPosition);
    doc.text(`Coach: ${displayCoachName}`, pageWidth / 2, yPosition);
    yPosition += 5;
    doc.text(`Academy: ${academyName}`, margin, yPosition);
    doc.text(`Date: ${reportDate}`, pageWidth / 2, yPosition);
    yPosition += 5;
    doc.text(`Sport: ${selectedSport}`, margin, yPosition);
    yPosition += 8;

    // Rating Scale Reference Information (matching Performance Metrics)
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Rating Scale Reference (For Information Only):', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    // Calculate the maximum width of level names for alignment
    const levelNames = [
      'Beginner (1-2):',
      'Intermediate (2-3):',
      'Advanced (3-4):',
      'Master (4-5):'
    ];
    doc.setFont('helvetica', 'bold');
    const maxLevelWidth = Math.max(...levelNames.map(name => doc.getTextWidth(name)));
    const definitionStartX = margin + 8 + maxLevelWidth + 5;
    
    // Beginner (1-2)
    doc.setFillColor(244, 67, 54);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Beginner (1-2):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Learning basic rules and shots', definitionStartX, yPosition);
    yPosition += 5;
    
    // Intermediate (2-3)
    doc.setFillColor(255, 152, 0);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Intermediate (2-3):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Can perform upto 5 consistent strokes but lacking accuracy and perfection', definitionStartX, yPosition);
    yPosition += 5;
    
    // Advanced (3-4)
    doc.setFillColor(33, 150, 243);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Advanced (3-4):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Can play 15 or more consistent strokes and do long rallies with different shots', definitionStartX, yPosition);
    yPosition += 5;
    
    // Master (4-5)
    doc.setFillColor(76, 175, 80);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Master (4-5):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Aggressive, Accurate and consistently performing in varying conditions', definitionStartX, yPosition);
    yPosition += 8;

    // Table column widths
    // Reduced: courtPosition, strokeAction by 30%
    // Increased: shotType, placement by 40%
    // Reduced: rating by 10% (for manual form)
    // Increased: strokeType by 10% (for manual form)
    const colWidths = {
      courtPosition: (pageWidth - 2 * margin) * 0.105,  // 0.15 * 0.7 = 0.105
      halfCourt: (pageWidth - 2 * margin) * 0.10,       // Keep same
      strokeType: (pageWidth - 2 * margin) * 0.144,     // 0.131 * 1.1 = 0.144
      strokeAction: (pageWidth - 2 * margin) * 0.105,    // 0.15 * 0.7 = 0.105
      shotType: (pageWidth - 2 * margin) * 0.168,        // 0.12 * 1.4 = 0.168
      placement: (pageWidth - 2 * margin) * 0.20,        // Slightly reduced
      rating: (pageWidth - 2 * margin) * 0.178,         // 0.181 * 0.9 = 0.163, adjusted to fit
    };
    
    const drawTableRow = (x: number, y: number, lineWidth: number = 0.2) => {
      doc.setLineWidth(lineWidth);
      let currentX = x;
      const lineY = y + rowHeight;
      const tableEndX = x + (pageWidth - 2 * margin);
      
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.courtPosition;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.halfCourt;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.strokeType;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.strokeAction;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.shotType;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.placement;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.rating;
      doc.line(currentX, y, currentX, lineY);
      
      doc.line(x, lineY, tableEndX, lineY);
    };

    const drawTableHeader = (y: number) => {
      const headerY = y;
      
      doc.setFillColor(245, 245, 245);
      doc.rect(tableStartX, headerY, pageWidth - 2 * margin, rowHeight, 'F');
      
      doc.setLineWidth(0.5);
      doc.line(tableStartX, headerY, tableStartX + (pageWidth - 2 * margin), headerY);
      
      drawTableRow(tableStartX, headerY, 0.3);
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      let textX = tableStartX;
      doc.text('Court Position', textX + colWidths.courtPosition / 2, headerY + 4, { align: 'center' });
      textX += colWidths.courtPosition;
      doc.text('Half Court', textX + colWidths.halfCourt / 2, headerY + 4, { align: 'center' });
      textX += colWidths.halfCourt;
      doc.text('Stroke Type', textX + colWidths.strokeType / 2, headerY + 4, { align: 'center' });
      textX += colWidths.strokeType;
      doc.text('Stroke Action', textX + colWidths.strokeAction / 2, headerY + 4, { align: 'center' });
      textX += colWidths.strokeAction;
      doc.text('Shot Type', textX + colWidths.shotType / 2, headerY + 4, { align: 'center' });
      textX += colWidths.shotType;
      doc.text('Placement', textX + colWidths.placement / 2, headerY + 4, { align: 'center' });
      textX += colWidths.placement;
      doc.text('Rating', textX + colWidths.rating / 2, headerY + 4, { align: 'center' });
    };

    // Helper function to get court position color
    const getCourtPositionColor = (courtPosition: string): number[] | null => {
      if (!courtPosition) return null;
      const pos = courtPosition.toLowerCase().trim();
      if (pos.includes('backcourt') || pos.includes('back court')) {
        return [232, 245, 232]; // Light green for backcourt
      }
      if (pos.includes('midcourt') || pos.includes('mid court')) {
        return [227, 242, 253]; // Light blue for midcourt
      }
      if (pos.includes('frontcourt') || pos.includes('front court')) {
        return [255, 243, 224]; // Light orange for frontcourt
      }
      return null;
    };

    const drawTableDataRow = (stroke: RankingStrokeType, y: number) => {
      const rowY = y;
      
      // Apply background color based on court position
      const bgColor = getCourtPositionColor(stroke.courtPosition);
      if (bgColor) {
        doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
        doc.rect(tableStartX, rowY, pageWidth - 2 * margin, rowHeight, 'F');
      }
      
      // Draw borders after background to ensure they're visible
      drawTableRow(tableStartX, rowY, 0.2);
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      let textX = tableStartX + 1;
      
      const courtPosition = doc.splitTextToSize(stroke.courtPosition || 'N/A', colWidths.courtPosition - 2);
      doc.text(courtPosition, textX, rowY + 4);
      
      textX += colWidths.courtPosition;
      const normalizedHalfCourt = normalizeHalfCourt(stroke.halfSide || 'N/A');
      const halfCourt = doc.splitTextToSize(normalizedHalfCourt, colWidths.halfCourt - 2);
      doc.text(halfCourt, textX, rowY + 4);
      
      textX += colWidths.halfCourt;
      const strokeType = doc.splitTextToSize(stroke.strokeType || 'N/A', colWidths.strokeType - 2);
      doc.text(strokeType, textX, rowY + 4);
      
      textX += colWidths.strokeType;
      const strokeAction = doc.splitTextToSize(stroke.strokeAction || 'N/A', colWidths.strokeAction - 2);
      doc.text(strokeAction, textX, rowY + 4);
      
      textX += colWidths.strokeAction;
      const shotType = doc.splitTextToSize(stroke.shotType || 'N/A', colWidths.shotType - 2);
      doc.text(shotType, textX, rowY + 4);
      
      textX += colWidths.shotType;
      const placement = doc.splitTextToSize(stroke.placement || 'N/A', colWidths.placement - 2);
      doc.text(placement, textX, rowY + 4);
      
      // Rating (empty boxes for manual filling - no stars filled)
      // Use smaller spacing to prevent overlap
      textX += colWidths.placement;
      const boxSpacing = 2.8; // Reduced from 3.5 to prevent overlap
      const boxSize = 2.2; // Slightly smaller boxes
      const startX = textX + 1; // Add small margin from placement column
      for (let i = 0; i < 5; i++) {
        doc.rect(startX + i * boxSpacing, rowY + 1.5, boxSize, boxSize);
      }
    };

    const addFooter = (doc: jsPDF, pageWidth: number, pageHeight: number, footerHeight: number) => {
      const footerY = pageHeight - footerHeight;
      
      // Draw footer line
      doc.setLineWidth(0.5);
      doc.line(10, footerY, pageWidth - 10, footerY);
      
      // Footer branding
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Encycdata', 10, footerY + 5);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('EncycGames', 10, footerY + 10);
      
      // Page number
      const pageNum = doc.getCurrentPageInfo().pageNumber;
      const totalPages = doc.getNumberOfPages();
      doc.setFontSize(8);
      doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, footerY + 7, { align: 'center' });
      
      // Date
      const footerDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      doc.text(footerDate, pageWidth - 10, footerY + 7, { align: 'right' });
    };

    // Start table
    let tableStartY = yPosition;
    drawTableHeader(tableStartY);
    yPosition += rowHeight;

    // Draw stroke types
    strokeTypes.forEach((stroke) => {
      if (yPosition + rowHeight > pageHeight - footerHeight) {
        addFooter(doc, pageWidth, pageHeight, footerHeight);
        doc.addPage();
        yPosition = headerHeight;
        tableStartY = yPosition;
        drawTableHeader(tableStartY);
        yPosition += rowHeight;
      }

      drawTableDataRow(stroke, yPosition);
      yPosition += rowHeight;
    });

    // Notes section
    if (yPosition + 30 > pageHeight - footerHeight) {
      addFooter(doc, pageWidth, pageHeight, footerHeight);
      doc.addPage();
      yPosition = headerHeight;
    } else {
      yPosition += 5;
    }
    
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', margin, yPosition);
    yPosition += 8;

    // Notes section without lines - just blank space
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    // Leave blank space for notes (no underline lines)
    yPosition += 20; // Add some space for notes

    addFooter(doc, pageWidth, pageHeight, footerHeight);

    // Save PDF with EncycGames prefix
    const fileName = `EncycGames_Ranking_Evaluation_Manual_Form_${categoryLabel.replace(/\s+/g, '_')}_${playerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('Error generating ranking evaluation manual form:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Full error details:', error);
    alert(`Error generating form: ${errorMessage}`);
    throw error; // Re-throw to allow caller to handle
  }
};

